import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Modal,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import {
  IconTrash,
  IconEdit,
  IconPlus
} from "@tabler/icons-react";
import { Context as AppContext } from "../../../../../context/AppContext";
import {v4 as uuid} from "uuid";
import CreateTagModal from "./CreateTagModal";

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  maxWidth: "640px",
  width: "95%",
  maxHeight: "80vh",
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  px: 4,
  py: 3,
  overflowY: "auto"
};

const TagManagementModal = ({ open, handleClose, tags = [], project }) => {
  const { updateProject } = useContext(AppContext);

  const [localTags, setLocalTags] = useState([]);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);

  // Reset local state when modal opens
  useEffect(() => {
    if (open) {
      setLocalTags(tags);
    }
  }, [open, tags]);

  const handleDelete = (uid) => {
    setLocalTags(prev => prev.filter(tag => tag.id !== uid));
  };

  const handleEdit = (tag) => {
    setSelectedTag(tag);
    setOpenCreateModal(true);
  };

  const handleAddNew = () => {
    setSelectedTag(null);
    setOpenCreateModal(true);
  };

  const handleTagSubmit = (tag) => {
    setLocalTags(prev => {
      const existingIndex = prev.findIndex(t => t.id === tag.id);
      if (existingIndex >= 0) {
        // Update existing tag
        const updated = [...prev];
        updated[existingIndex] = tag;
        return updated;
      } else {
        // Add new tag
        return [...prev, tag];
      }
    });
  };

  const handleTagRemove = (id) => {
    handleDelete(id);
  };

  const handleSaveChanges = async () => {
    const newProject = {
      ...project,
      settings: {
        ...project.settings,
        tags: localTags
      }
    };

    await updateProject(newProject);
    handleClose();
  };

  return (
    <>
      <CreateTagModal
        open={openCreateModal}
        handleClose={() => {
          setOpenCreateModal(false);
          setSelectedTag(null);
        }}
        tag={selectedTag}
        handleSubmit={handleTagSubmit}
        onRemove={handleTagRemove}
        project={project}
      />
      <Modal open={open} onClose={handleClose}>
        <Stack sx={style} gap={2}>
          <Typography variant="h6" fontWeight={500}>Manage Tags</Typography>

          <Box>
            <Stack direction="row" gap={1} mb={2} justifyContent="flex-end">
              <Button onClick={handleAddNew} variant="modal" startIcon={<IconPlus />}>
                Add Tag
              </Button>
            </Stack>

            {/* Tag Table */}
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {localTags.map((tag) => (
                  <TableRow key={tag.id}>
                    <TableCell>{tag.name}</TableCell>
                    <TableCell>{tag.title || "-"}</TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => handleEdit(tag)}>
                        <IconEdit size={18} />
                      </IconButton>
                      <IconButton onClick={() => handleDelete(tag.id)}>
                        <IconTrash size={18} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          {/* Footer Buttons */}
          <Stack direction="row" justifyContent="flex-end" gap={1} mt={2}>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              onClick={handleSaveChanges}
              variant="modal"
              disabled={!localTags.length}
            >
              Save Changes
            </Button>
          </Stack>
        </Stack>
      </Modal>
    </>
  );
};

export default TagManagementModal;
